import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Empty, Form, Tooltip, Typography } from 'antd';

import type { ModelProduct, ModelSelectProducts } from '@app/api/Products';
import {
  goodsWriteOffColumns,
  PF,
} from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/data';
import { ProductCountInput } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/ui/shared/ProductCountInput';
import { ProductNameCell } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/ui/shared/ProductNameCell';

import type { FormInstance } from 'antd';

const TABLE = `${PF}_products-table`;
const { Item, List } = Form;
const { Text } = Typography;

interface Props {
  form: FormInstance;
  products?: ModelProduct[];
  warehouseProducts: ModelSelectProducts[];
  onDelete: (productId: number) => void;
  isAddDisabled: boolean;
  isPageLoading: boolean;
  isMutationLoading: boolean;
  onOpenModal: () => void;
  totalCount: number;
}

export const AvailableProductsSection = ({
  form,
  products,
  warehouseProducts,
  onDelete,
  isAddDisabled,
  isPageLoading,
  isMutationLoading,
  onOpenModal,
  totalCount,
}: Props) => (
  <div className={`${TABLE}_container`}>
    <List name="Products">
      {(fields, { remove }) => {
        const hasProducts = fields.length > 0;
        return (
          <div className={`${TABLE}_scroll-wrapper`}>
            <table className={TABLE}>
              <thead className={`${TABLE}_thead`}>
                <tr>
                  {goodsWriteOffColumns.map(({ id, title, className }) => (
                    <th
                      key={id}
                      className={`${TABLE}_th ${TABLE}_${className}`}
                    >
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hasProducts ? (
                  fields.map((field, index) => {
                    const { name, key } = field;
                    const product = form.getFieldValue(['Products', name]);
                    const productData = warehouseProducts.find(
                      (p) => p.ProductId === product?.ProductId
                    );
                    const extraProductData = products?.find(
                      (p) => p.Id === product?.ProductId
                    );

                    return (
                      <tr key={key} className={`${TABLE}_row`}>
                        <td className={`${TABLE}_cell`}>
                          <span className="no-wrap-text">{index + 1}</span>
                        </td>
                        <td className={`${TABLE}_cell`}>
                          <ProductNameCell
                            productId={product?.ProductId}
                            name={extraProductData?.Name || 'Неизвестный товар'}
                          />
                        </td>
                        <td className={`${TABLE}_cell`}>
                          <Text type="secondary" className="no-wrap-text">
                            {productData?.Supplier || 'Неизвестный поставщик'}
                          </Text>
                        </td>
                        <td className={`${TABLE}_cell`}>
                          <ProductCountInput fieldName={name} form={form} />
                        </td>
                        <td className={`${TABLE}_cell`}>
                          <Item>
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                remove(name);
                                onDelete(product?.ProductId);
                              }}
                            />
                          </Item>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <Empty
                        className={`${TABLE}_empty`}
                        description="Товары не выбраны"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className={`${TABLE}_footer`}>
                  <td className={`${TABLE}_footer_cell`} colSpan={5}>
                    <div className={`${TABLE}_footer_buttons`}>
                      <Tooltip title={isAddDisabled ? 'Выберите склад' : ''}>
                        <Button
                          type="primary"
                          onClick={onOpenModal}
                          icon={<PlusOutlined />}
                          className={`${TABLE}_footer_buttons_item`}
                          disabled={
                            isAddDisabled || isPageLoading || isMutationLoading
                          }
                        >
                          Добавить товар
                        </Button>
                      </Tooltip>
                      <Button
                        icon={<FontAwesomeIcon icon={faFileExcel} />}
                        className={`${TABLE}_footer_buttons_item`}
                        disabled={isPageLoading || isMutationLoading}
                      >
                        Загрузить из Excel
                      </Button>
                    </div>
                  </td>
                </tr>
                {hasProducts && (
                  <tr className={`${TABLE}_footer`}>
                    <td className={`${TABLE}_footer_cell`} />
                    <td className={`${TABLE}_footer_cell`}>
                      <Text strong>Итого:</Text>
                    </td>
                    <td className={`${TABLE}_footer_cell`}>
                      <Text strong style={{ padding: 12 }}>
                        {totalCount}
                      </Text>
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        );
      }}
    </List>
  </div>
);
